// Form intelligence and handling module

import type { WaitlistForm } from "../../types";

export class FormManager {
  private attendeeSelect: HTMLSelectElement | null;
  private discountSelect: HTMLSelectElement | null;
  private waitlistForm: HTMLFormElement | null;

  constructor() {
    this.attendeeSelect = document.getElementById(
      "attendee_count"
    ) as HTMLSelectElement | null;
    this.discountSelect = document.getElementById(
      "discount_type"
    ) as HTMLSelectElement | null;
    this.waitlistForm = document.getElementById(
      "waitlistForm"
    ) as HTMLFormElement | null;

    this.init();
  }

  private init(): void {
    this.initFormEventListeners();
    this.initParameterHandler();
    this.initSubmissionHandler();
  }

  private initFormEventListeners(): void {
    // Add event listener for attendee count changes to auto-update discount
    if (this.attendeeSelect && this.discountSelect) {
      this.attendeeSelect.addEventListener("change", () => {
        const newAttendeeCount = parseInt(this.attendeeSelect!.value, 10);
        const suggestedDiscount = this.detectDiscountType(newAttendeeCount);
        this.discountSelect!.value = suggestedDiscount;
      });
    }
  }

  private initParameterHandler(): void {
    // Listen for URL parameter events from navigation module
    window.addEventListener("navigation:urlParams", (event: Event) => {
      const customEvent = event as CustomEvent;
      this.handleUrlParameters(customEvent.detail.params);
    });
  }

  private handleUrlParameters(params: string): void {
    const urlParams = new URLSearchParams(params);
    const attendeeCount = urlParams.get("attendee_count");

    if (attendeeCount && this.attendeeSelect && this.discountSelect) {
      // Wait a bit for the scroll to complete, then pre-select the attendee count
      setTimeout(() => {
        this.attendeeSelect!.value = attendeeCount;
        this.addVisualFeedback(this.attendeeSelect!);

        // Auto-detect and set discount type based on attendee count
        const detectedDiscount = this.detectDiscountType(
          parseInt(attendeeCount, 10)
        );
        this.discountSelect!.value = detectedDiscount;
        this.addVisualFeedback(this.discountSelect!);
      }, 500);
    }
  }

  private addVisualFeedback(element: HTMLSelectElement): void {
    element.style.backgroundColor = "#fef3c7"; // Light yellow highlight
    element.style.transition = "background-color 2s ease";

    // Remove highlight after 2 seconds
    setTimeout(() => {
      element.style.backgroundColor = "";
    }, 2000);
  }

  private detectDiscountType(attendeeCount: number): string {
    // Handle special cases first
    if (attendeeCount === 2) {
      return "couples";
    } else if (attendeeCount >= 10) {
      return "group_10";
    } else if (attendeeCount >= 5) {
      return "group_5";
    } else {
      // For 1 person, 3-4 people -> Early bird
      return "early_bird";
    }
  }

  private initSubmissionHandler(): void {
    if (!this.waitlistForm) return;

    this.waitlistForm.addEventListener("submit", async (e: Event) => {
      e.preventDefault();
      await this.handleFormSubmission();
    });
  }

  private async handleFormSubmission(): Promise<void> {
    if (!this.waitlistForm) return;

    // Temporarily enable discount field to include its value in form submission
    if (this.discountSelect) {
      this.discountSelect.disabled = false;
    }

    // get form data
    const formData = new FormData(this.waitlistForm);

    // Re-disable discount field after getting form data
    if (this.discountSelect) {
      this.discountSelect.disabled = true;
    }

    // Parse attendee count safely
    const attendeeCountStr = formData.get("attendee_count")?.toString() || "1";
    const attendeeCountNum = parseInt(attendeeCountStr, 10);
    const attendeeCount = isNaN(attendeeCountNum) ? 1 : attendeeCountNum;

    const data: WaitlistForm = {
      firstName: formData.get("firstName")?.toString() || "",
      lastName: formData.get("lastName")?.toString() || "",
      email: formData.get("email")?.toString() || "",
      role: formData.get("role")?.toString() || "",
      company: formData.get("company")?.toString() || "",
      experience: formData.get("experience")?.toString() || undefined,
      interest: formData.get("interest")?.toString() || undefined,
      challenges: formData.get("challenges")?.toString() || undefined,
      linkedin: formData.get("linkedin")?.toString() || undefined,
      attendee_count: attendeeCount,
      discount_type:
        (formData.get("discount_type")?.toString() as any) || "none",
    };

    // Simple validation
    if (
      !data.firstName ||
      !data.lastName ||
      !data.email ||
      !data.role ||
      !data.company
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    // Show loading state
    const submitBtn = this.waitlistForm.querySelector(
      ".submit-btn"
    ) as HTMLButtonElement | null;
    if (!submitBtn) return;

    const originalText = submitBtn.textContent;
    submitBtn.textContent = "Submitting...";
    submitBtn.disabled = true;

    try {
      // Submit to Cloudflare Worker
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        // Redirect to success page instead of showing alert
        window.location.href = "/success.html";
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      console.error("Error:", error);
      alert(
        "There was an error submitting your information. Please try again."
      );
    } finally {
      // Reset button state
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }
  }
}
